import React from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import DoorCta from './DoorCta';
import useDoorPage, { studioHrefFor } from '../hooks/useDoorPage';
import { DOOR_DEFLICKER } from '../doorAttribution';
import { RETENTION_DAYS } from '../photoUpload/caps';
import { PRICING_TIERS, FREE_SIGNUP_CREDITS } from '../pricing/plans';
import { displayNameOf } from '../lib/operations';
import './DoorPage.css';

// Deflicker door (#387). The flagship acquisition page: "deflicker timelapse
// online", "timelapse flicker fix free", "remove timelapse flicker no download"
// are the highest-intent queries in the category, and every incumbent answer is
// a desktop install. Browser + free-to-try is the whole wedge, so the page
// leads with "nothing to download" rather than with a feature list.
//
// Honesty contract (#378/#384): every capability named here is reachable in the
// shipped UI, and every number is imported from the module that ENFORCES it
// (caps.js, pricing/plans.js) rather than retyped, so a limit change can never
// leave a stale promise on a marketing page. The operation name comes from the
// registry (#400/#379) for the same reason — the page cannot drift from what
// the Edits tab actually calls the control.
//
// Doors are CONTENT ONLY: no file input, no upload, no processing surface. A
// visitor signs in and works in the studio (product rule, locked 2026-07-27).

const FREE_TIER = PRICING_TIERS.find((tier) => tier.id === 'free');
const SOLO_TIER = PRICING_TIERS.find((tier) => tier.id === 'solo_founding');

const FREE_POOL_LABEL = `${FREE_TIER.storageGb} GB`;

// The user-facing control name, read from the naming module the Edits cards
// render (#379). Displays as "Fix flicker"; the slug stays "deflicker" because
// that is the word people search.
const FIX_FLICKER = displayNameOf('deflicker');

const SECTION_IDS = [
  'door_hero',
  'door_causes',
  'door_how',
  'door_inputs',
  'door_free',
  'door_faq',
  'door_final_cta',
];

const TOC = [
  { id: 'door_causes', label: 'What causes timelapse flicker' },
  { id: 'door_how', label: `How ${FIX_FLICKER} works in Tweedia` },
  { id: 'door_inputs', label: 'Video files and photo sequences both work' },
  { id: 'door_free', label: 'What the free account includes' },
  { id: 'door_faq', label: 'FAQ' },
];

const CAUSES = [
  {
    term: 'Aperture variation between shots',
    text:
      'On most lenses the aperture blades close mechanically for every exposure and do not land in exactly the same place twice. Each frame gets a fraction of a stop more or less light than the one before it, which is invisible in a single photo and reads as a strobe at 24 frames per second.',
  },
  {
    term: 'Automatic exposure',
    text:
      'If the camera re-meters each frame, anything that moves through the scene — a cloud, a vehicle, a person in a bright jacket — shifts the metering and therefore the brightness of the whole frame. The correction lands between two consecutive frames instead of gradually.',
  },
  {
    term: 'Mixed and mains-powered lighting',
    text:
      'Lights running on AC mains cycle at 50 or 60 Hz. A shutter faster than that cycle samples a different point of it on every frame, so indoor and jobsite footage picks up a beat that has nothing to do with the light you can see.',
  },
  {
    term: 'Auto white balance and auto ISO',
    text:
      'Both drift frame to frame for the same reason exposure does, and both change the brightness of the image. On a long shoot they add a slow wobble underneath the fast flicker.',
  },
];

const STEPS = [
  {
    name: 'Create a free account',
    text:
      'No credit card, nothing to install, no browser plugin. Sign up free in your browser — one project holds your footage, your edits and your finished renders, and you can pick it up from any browser you sign in on.',
  },
  {
    name: 'Upload your footage into a project',
    text:
      'One project per shoot. Upload the finished timelapse as an .mp4, or upload the folder of stills your camera left you and let Tweedia compile it into a video first.',
  },
  {
    name: `Switch on ${FIX_FLICKER} on the Edits tab`,
    text: `Flicker is measured automatically while your footage is analyzed; when it is found the card flags it, and you switch on ${FIX_FLICKER}. Your choice sticks from then on.`,
  },
  {
    name: 'Render and download',
    text:
      'The render runs on our workers, so you can close the tab and come back. The finished video is yours to download; renders on the free plan carry a Tweedia watermark.',
  },
];

const FAQ = [
  {
    q: 'Do I have to download or install anything?',
    a: 'No. Tweedia Studio runs in the browser — you sign in, upload your footage, and the rendering happens on our servers. There is no desktop application, no plugin and no command line, on Windows, macOS, Linux or ChromeOS.',
  },
  {
    q: 'Is it really free?',
    a: `The account is free and comes with ${FREE_TIER.storageGb} GB of pooled storage and ${FREE_SIGNUP_CREDITS} starter credits, which is what pays for rendering finished videos. Renders on the free plan carry a Tweedia watermark, and raw photos are cleared ${RETENTION_DAYS} days after a project's last activity. ${SOLO_TIER.name}, at ${SOLO_TIER.priceLabel}${SOLO_TIER.priceSuffix}, removes the watermark and raises the storage pool.`,
  },
  {
    q: 'Can I tell whether it worked before I spend anything?',
    a: `Yes. Once your footage has been analyzed, the ${FIX_FLICKER} card draws the frame-to-frame brightness of your own clip and the smoothed curve the filter will produce, side by side. It is computed in your browser from the measurements already stored, so it costs no credits and needs no render.`,
  },
  {
    q: 'Does it work on Brinno footage?',
    a: 'Yes — Brinno construction and garden cameras meter every frame independently over months of changing light, which is exactly the pattern this smooths. The Brinno guide walks through the upload step, including what to do if your model wrote an AVI file.',
  },
  {
    q: 'Does it work on GoPro footage?',
    a: 'Yes. Upload a Time Lapse Video .mp4 straight into a project. If you shot in Time Lapse Photo mode you have a folder of JPGs instead — compile them into a video first, which the GoPro guide covers step by step.',
  },
  {
    q: 'What can I upload?',
    a: 'Video goes in as .mp4. Stills go in as JPEG, PNG, HEIC/HEIF, TIFF, WebP or AVIF — drop the folder as it came off the card, and non-image files are skipped and reported rather than failing the batch.',
  },
  {
    q: 'Will it flatten a sunset or a day-to-night shot?',
    a: `No. ${FIX_FLICKER} pulls each frame's brightness toward the average of a short window of neighbouring frames, so it removes the frame-to-frame jitter and leaves the slow change in light across the shoot alone. It is not an exposure ramp and it does not grade your footage.`,
  },
  {
    q: 'Will it fix banding — the dark rolling bars from LED or fluorescent lights?',
    a: 'No, and it is worth being clear about it: banding is a difference in brightness within a single frame, caused by the sensor reading out while the light is still cycling. This smooths differences between whole frames. Banding has to be fixed at capture, with a shutter speed matched to the mains frequency.',
  },
];

function buildJsonLd() {
  const url = 'https://tweediastudio.com/deflicker-timelapse';
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        name: 'Deflicker a Timelapse Online',
        description:
          'Remove timelapse flicker in your browser. Upload an MP4 or a photo sequence, switch on Fix flicker, and render — no download, no plugin.',
        url,
        publisher: {
          '@type': 'Organization',
          name: 'Tweedia Studio',
          url: 'https://tweediastudio.com',
        },
      },
      {
        '@type': 'HowTo',
        '@id': `${url}#howto`,
        name: 'How to deflicker a timelapse online',
        description:
          'Upload your timelapse to a Tweedia Studio project, switch on Fix flicker on the Edits tab, and render the smoothed video.',
        step: STEPS.map((step, index) => ({
          '@type': 'HowToStep',
          position: index + 1,
          name: step.name,
          url: `${url}#door_how`,
          text: step.text,
        })),
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: FAQ.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };
}

const STUDIO_HREF = studioHrefFor(DOOR_DEFLICKER);

function DeflickerPage({ user, signIn }) {
  const signedIn = !!user;

  const { studioHref, setSectionRef, onCtaActivate, onNavClick } = useDoorPage({
    door: DOOR_DEFLICKER,
    signedIn,
    signIn,
    sectionIds: SECTION_IDS,
    meta: {
      title: 'Deflicker a Timelapse Online — Free, In Your Browser | Tweedia Studio',
      description: `Fix timelapse flicker in your browser — no download, no plugin. Upload an MP4 or a photo sequence, switch on ${FIX_FLICKER}, and render. Free account, watermarked output.`,
      canonicalPath: '/deflicker-timelapse',
      jsonLd: buildJsonLd(),
    },
  });

  const renderCta = (ctaId, label, testId) => (
    <DoorCta
      ctaId={ctaId}
      testId={testId}
      signedIn={signedIn}
      studioHref={studioHref}
      onActivate={onCtaActivate}
    >
      {label}
    </DoorCta>
  );

  return (
    <div className="door-page">
      <Header user={user} signIn={signIn} />
      <main className="door-content" data-testid="door-content">
        <section
          className="door-hero"
          id="door_hero"
          data-section-id="door_hero"
          ref={setSectionRef('door_hero')}
        >
          <p className="door-eyebrow">Deflicker timelapse</p>
          <h1>Deflicker a Timelapse Online</h1>
          <p className="door-lede">
            Flicker is the frame-to-frame brightness jitter that makes an otherwise smooth
            timelapse strobe. Tweedia Studio smooths it in your browser — nothing to download,
            no plugin, no desktop app. Create a free account, upload the clip, switch on{' '}
            {FIX_FLICKER}, and render.
          </p>
          <div className="door-cta-row">
            {renderCta('door_deflicker_hero', 'Start free — fix your flicker', 'door-cta-hero')}
          </div>
          <p className="door-note">
            Free account, no credit card. Renders on the free plan carry a Tweedia watermark.
          </p>
        </section>

        <nav className="door-toc" aria-label="On this page">
          <p className="door-toc-title">On this page</p>
          <ol>
            {TOC.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`}>{item.label}</a>
              </li>
            ))}
          </ol>
        </nav>

        <section
          id="door_causes"
          data-section-id="door_causes"
          ref={setSectionRef('door_causes')}
        >
          <h2>What causes timelapse flicker</h2>
          <p>
            Flicker is not one problem. It is any variation in exposure between consecutive
            frames that was too small to notice while you were shooting and is impossible to
            miss once those frames play back a hundred times faster.
          </p>
          <dl className="door-causes" data-testid="door-causes">
            {CAUSES.map((cause) => (
              <React.Fragment key={cause.term}>
                <dt>{cause.term}</dt>
                <dd>{cause.text}</dd>
              </React.Fragment>
            ))}
          </dl>
          <p>
            Every one of them is fixable at capture — a manual lens, fixed exposure, a shutter
            matched to the mains — and none of that helps footage you have already shot. In
            post, the fix is temporal: pull each frame's brightness toward the average of the
            frames around it, so the jitter flattens while the real change in light survives.
            That is what {FIX_FLICKER} does.
          </p>
        </section>

        <section id="door_how" data-section-id="door_how" ref={setSectionRef('door_how')}>
          <h2>How {FIX_FLICKER} works in Tweedia</h2>
          <ol className="door-steps" data-testid="door-steps">
            {STEPS.map((step) => (
              <li key={step.name}>
                <strong>{step.name}.</strong> {step.text}
              </li>
            ))}
          </ol>
        </section>

        <section
          id="door_inputs"
          data-section-id="door_inputs"
          ref={setSectionRef('door_inputs')}
        >
          <h2>Video files and photo sequences both work</h2>
          <p>
            <strong>If your camera wrote a video</strong> — a GoPro Time Lapse Video clip, a
            Brinno MP4, a phone timelapse — upload the .mp4 into a project and go straight to
            the Edits tab.
          </p>
          <p>
            <strong>If your camera left you stills</strong> — an interval run, a Time Lapse
            Photo folder, a trail camera card — upload the folder instead. Tweedia orders the
            frames by the time each one was actually captured and compiles them into a video,
            which then edits exactly like an uploaded clip. Photo sequences flicker more often
            than finished clips do, because every frame was metered on its own.
          </p>
          <div className="door-crosslinks" data-testid="door-crosslinks">
            <Link
              to="/photo-sequence-to-video"
              className="door-crosslink"
              data-testid="door-link-photo-sequence"
              onClick={onNavClick('door_photo_sequence_from_deflicker')}
            >
              <span className="door-crosslink-title">Photo sequence to video →</span>
              <span className="door-crosslink-text">
                Turn a folder of stills into a timelapse first, then fix the flicker.
              </span>
            </Link>
            <Link
              to="/guides/brinno-timelapse-flicker"
              className="door-crosslink"
              data-testid="door-link-brinno"
              onClick={onNavClick('guide_brinno_from_deflicker')}
            >
              <span className="door-crosslink-title">Brinno timelapse flicker →</span>
              <span className="door-crosslink-text">
                Why long-deployment construction cams strobe, and how to fix the clip.
              </span>
            </Link>
            <Link
              to="/guides/gopro-time-lapse-photos-to-video"
              className="door-crosslink"
              data-testid="door-link-gopro"
              onClick={onNavClick('guide_gopro_from_deflicker')}
            >
              <span className="door-crosslink-title">GoPro Time Lapse Photos to video →</span>
              <span className="door-crosslink-text">
                Compile the JPG folder GoPro leaves on the card, then smooth it.
              </span>
            </Link>
          </div>
        </section>

        <section id="door_free" data-section-id="door_free" ref={setSectionRef('door_free')}>
          <h2>What the free account includes</h2>
          <ul className="door-free-list" data-testid="door-free-list">
            <li>
              <strong>{FIX_FLICKER} on any project</strong>, with no feature gate — it is not
              held back for paid accounts.
            </li>
            <li>
              <strong>{FREE_POOL_LABEL} of pooled storage</strong> shared across unlimited
              projects. Raw stills, source video and renders all count against it.
            </li>
            <li>
              <strong>{FREE_SIGNUP_CREDITS} starter credits</strong>. Credits pay for rendering
              finished videos, and a longer or higher-resolution render uses more of them.
            </li>
            <li>
              <strong>Renders carry a Tweedia watermark</strong> on the free plan. That — not
              resolution — is the free limit: every plan exports up to 4K.
            </li>
            <li>
              <strong>Raw photos are kept {RETENTION_DAYS} days</strong> after a project's last
              activity on the free plan, then cleared. Your latest render of an edit stays
              available — only the raw stills age out.
            </li>
          </ul>
          <p>
            {SOLO_TIER.name}, at {SOLO_TIER.priceLabel}
            {SOLO_TIER.priceSuffix}, removes the watermark, raises the pool to{' '}
            {SOLO_TIER.storageGb} GB and keeps your raw photos while the subscription is
            active.{' '}
            <Link to="/pricing" onClick={onNavClick('pricing_from_deflicker_door')}>
              See the plans
            </Link>
            .
          </p>
        </section>

        <section id="door_faq" data-section-id="door_faq" ref={setSectionRef('door_faq')}>
          <h2>FAQ</h2>
          <div className="door-faq" data-testid="door-faq">
            {FAQ.map((item) => (
              <div className="door-faq-item" key={item.q}>
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section
          className="door-final-cta"
          id="door_final_cta"
          data-section-id="door_final_cta"
          ref={setSectionRef('door_final_cta')}
        >
          <h2>Bring the flickery clip</h2>
          <p>
            Upload it, look at the before-and-after brightness curve, and render when it looks
            right. Same browser, same tab, no install.
          </p>
          <div className="door-cta-row">
            {renderCta('door_deflicker_final', 'Create your free account', 'door-cta-final')}
            <Link
              to="/lrtimelapse-alternative"
              className="door-secondary-cta"
              data-testid="door-link-lrt"
              onClick={onNavClick('lrt_alternative_from_deflicker')}
            >
              Compare with LRTimelapse
            </Link>
          </div>
          <p className="door-note">
            Free renders carry a Tweedia watermark. No credit card required to start.
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default DeflickerPage;
export { FAQ, STEPS, CAUSES, SECTION_IDS, STUDIO_HREF, FIX_FLICKER };
